<template>
  <FreeBottomSheet :name="NPS_SURVEY_BOTTOM_SHEET_NAME" @onClose="onClose">
    <template #header="{ hide }">
      <div class="relative">
        <h2
          class="text-text-neutral-primary px-4 pb-3 pt-4 text-center text-lg font-semibold"
        >
          {{ t('npsSurvey.title') }}
        </h2>
        <FreeCloseButton
          class="gray-header-close-button absolute right-4 top-4"
          @click="hide"
        />
      </div>
    </template>
    <template #default>
      <div class="px-4 pb-4 pt-3">
        <template v-if="rating > 0">
          <div class="space-y-6">
            <div class="space-y-3">
              <div
                class="bg-background-neutral-secondary space-y-4 rounded-2xl px-4 pb-6 pt-4"
              >
                <div
                  class="text-text-neutral-primary text-center font-semibold"
                >
                  {{ t(`npsSurvey.rating.${rating}.label`) }}
                </div>
                <NpsSurveyStarRating v-model="rating" />
              </div>
              <div
                class="bg-background-neutral-secondary space-y-4 rounded-2xl p-4"
              >
                <div class="text-text-neutral-primary font-semibold">
                  {{ t('npsSurvey.commentLabel') }}
                </div>
                <FreeTextarea
                  v-model="comment"
                  :rows="5"
                  :placeholder="
                    t(`npsSurvey.rating.${rating}.commentPlaceholder`)
                  "
                  :maxlength="NPS_COMMENT_MAX_LENGTH"
                />
              </div>
              <template v-if="rating <= 1">
                <div
                  class="bg-background-neutral-secondary space-y-4 rounded-2xl p-4"
                >
                  <div class="flex items-center justify-between">
                    <div class="text-text-neutral-primary font-medium">
                      {{ t('npsSurvey.wantSupportContact') }}
                    </div>
                    <FreeSwitch v-model="needAssistance" />
                  </div>
                  <template v-if="needAssistance">
                    <FreeInput
                      v-maska:[inputsMasks.phoneNumber]
                      type="tel"
                      :model-value="phoneNumber"
                      :label="t('npsSurvey.phoneLabel')"
                      :placeholder="t('npsSurvey.phonePlaceholder')"
                      :has-error="!!phoneValidationError"
                      @input="
                        phoneNumber = ($event.target as HTMLInputElement).value;
                        phoneValidationError = null;
                      "
                    >
                      <template v-if="phoneValidationError" #errorMessage>
                        <p>{{ phoneValidationError }}</p>
                      </template>
                    </FreeInput>
                  </template>
                </div>
              </template>
            </div>
            <FreeButton class="green-main-button w-full" @click="onSubmit">
              {{ t('npsSurvey.submit') }}
            </FreeButton>
          </div>
        </template>
        <template v-else>
          <div class="space-y-4 px-4">
            <div class="text-text-neutral-primary text-center">
              {{ t('npsSurvey.question') }}
            </div>
            <NpsSurveyStarRating v-model="rating" />
          </div>
        </template>
      </div>
    </template>
  </FreeBottomSheet>
</template>

<script setup lang="ts">
  import { ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { controllerModel, validationRulesLib } from 'shared';
  import {
    FreeBottomSheet,
    FreeCloseButton,
    FreeTextarea,
    FreeButton,
    FreeSwitch,
    FreeInput,
    aviaMasksOptions,
  } from 'freedom-shared';

  import { bloomreachLib, bloomreachEventsConfig } from '@/shared/lib';
  import { useAlert } from '@/shared/ui';

  import {
    NPS_SURVEY_BOTTOM_SHEET_NAME,
    NPS_COMMENT_MAX_LENGTH,
  } from '../config';
  import NpsSurveyStarRating from './NpsSurveyStarRating.vue';

  const { t } = useI18n();

  const bottomSheet = controllerModel.useBottomSheet();
  const alert = useAlert();

  /**
   * Options must be assigned inside the script where it's used.
   * @see https://github.com/beholdr/maska/issues/149#issuecomment-1529619595
   */
  const inputsMasks = aviaMasksOptions;

  const rating = ref(0);
  const comment = ref('');

  const needAssistance = ref(false);
  const phoneNumber = ref('');
  const phoneValidationError = ref<string | null>(null);
  const hasSubmitted = ref(false);

  watch(needAssistance, value => {
    if (!value) phoneValidationError.value = null;
  });

  function trackDismiss() {
    bloomreachLib.track(
      bloomreachEventsConfig.BloomreachEventName.NPS_SURVEY_DISMISSED,
    );
  }

  function onClose() {
    if (!hasSubmitted.value) {
      trackDismiss();
    }
  }

  function validatePhoneWhenNeeded(): boolean {
    if (!needAssistance.value) return true;
    phoneValidationError.value = null;
    const trimmed = phoneNumber.value.trim();
    if (!trimmed) {
      phoneValidationError.value = t('npsSurvey.validations.required');
      return false;
    }
    if (!validationRulesLib.isValidPhoneNumber(trimmed)) {
      phoneValidationError.value = t(
        'npsSurvey.validations.invalidPhoneNumber',
      );
      return false;
    }
    return true;
  }

  function onSubmit() {
    if (!validatePhoneWhenNeeded()) return;
    hasSubmitted.value = true;
    bloomreachLib.track(
      bloomreachEventsConfig.BloomreachEventName.NPS_SURVEY_SUBMITTED,
      {
        rating: rating.value,
        ...(comment.value && { comment: comment.value }),
        ...(needAssistance.value && { phone_number: phoneNumber.value.trim() }),
      },
    );

    alert.show({
      type: 'heart',
      message: t(
        `npsSurvey.rating.${rating.value}.${
          comment.value.length > 0 ? 'commented' : 'submitted'
        }`,
      ),
      duration: 3000,
    });

    bottomSheet.hide(NPS_SURVEY_BOTTOM_SHEET_NAME);
  }
</script>
